// Pure rules of the annual MRMR forecasts tab (feature 19470).
// No imports on purpose: tests/unit/annualForecastRules.spec.mjs loads this file under plain Node.

export const ELEMENT = { Gold: 0, Silver: 1, Copper: 2, Zinc: 3, Lead: 4, Carbon: 5, Sulfur: 6 };

// Mirrors AEM.Geo.Business.SampleUnit.
export const UNIT = { None: 0, Percent: 1, GramPerTon: 2, kCountPerSec: 3, kgPerM3: 4, mg: 5, ppm: 6, Unknown: 7, Gram: 8, Kilogram: 9 };

// Mirrors AEM.Geo.Business.MRMRResourceType (CAT column of the MRMR workbook).
export const RESOURCE_TYPE = { PotentialMineral: 0, Inferred: 1, Indicated: 2, Measured: 3, Probable: 4, Proven: 5 };

// Elements offered when the project declares none.
export const DEFAULT_ELEMENTS = [ELEMENT.Gold, ELEMENT.Silver, ELEMENT.Copper, ELEMENT.Zinc, ELEMENT.Lead];

export function isBlank(value) {
  return value === null || value === undefined || value === '';
}

function valueOrNull(value) {
  return value === undefined ? null : value;
}

// Precious metals are stated in g/t, base metals in %.
export function defaultUnitFor(element) {
  return element === ELEMENT.Gold || element === ELEMENT.Silver ? UNIT.GramPerTon : UNIT.Percent;
}

// An annual forecast covers the calendar year; dates travel as yyyy-MM-dd (no time zone shift).
export function yearPeriod(year) {
  return { startDate: `${year}-01-01`, endDate: `${year}-12-31` };
}

export function yearOf(forecast) {
  if (!forecast || !forecast.startDate) return null;
  return Number(String(forecast.startDate).slice(0, 4));
}

// Newest first, so the current year sits near the top of the picker.
export function yearOptions(currentYear, yearsBack = 5, yearsForward = 1) {
  const years = [];
  for (let year = currentYear + yearsForward; year >= currentYear - yearsBack; year--) {
    years.push(year);
  }
  return years;
}

export function findResult(results, element) {
  if (!Array.isArray(results)) return null;
  return results.find(r => r.element === element) || null;
}

// Grade shown for a result. An imported metal quantity is divided by the forecast tonnage (g → g/t, kg → %);
// otherwise the grade entered by hand is shown as stored. Returns { value, unit } or null.
export function gradeOf(result, tonnage) {
  if (!result) return null;
  if (!isBlank(result.quantity)) {
    if (!tonnage || tonnage <= 0) return null;
    if (result.quantityUnit === UNIT.Gram) return { value: result.quantity / tonnage, unit: UNIT.GramPerTon };
    if (result.quantityUnit === UNIT.Kilogram) return { value: result.quantity / tonnage / 10, unit: UNIT.Percent };
    return null;
  }
  if (!isBlank(result.grade)) return { value: result.grade, unit: result.gradeUnit };
  return null;
}

// Form entries to the results sent to the API. A grade typed in the form is stored and replaces any imported
// quantity (manual override); an entry left blank keeps its imported quantity untouched; an entry with neither
// yields no result, so a missing value never becomes a zero.
export function buildResults(entries) {
  return (entries || []).reduce((results, entry) => {
    const hasGrade = !isBlank(entry.grade) && !Number.isNaN(Number(entry.grade));
    if (hasGrade) {
      results.push({ element: entry.element, grade: Number(entry.grade), gradeUnit: entry.gradeUnit, quantity: null, quantityUnit: null });
    } else if (!isBlank(entry.quantity)) {
      results.push({ element: entry.element, grade: null, gradeUnit: null, quantity: entry.quantity, quantityUnit: entry.quantityUnit });
    }
    return results;
  }, []);
}

// One form entry per element, pre-filled from an existing forecast or empty with the element's default unit.
export function buildEntries(elements, results) {
  const list = elements && elements.length > 0 ? elements : DEFAULT_ELEMENTS;
  return list.map(element => {
    const existing = findResult(results, element);
    return {
      element,
      grade: existing ? valueOrNull(existing.grade) : null,
      gradeUnit: existing && !isBlank(existing.gradeUnit) ? existing.gradeUnit : defaultUnitFor(element),
      quantity: existing ? valueOrNull(existing.quantity) : null,
      quantityUnit: existing ? valueOrNull(existing.quantityUnit) : null
    };
  });
}

export function isApproved(forecast) {
  return !!forecast && forecast.approbationDate !== null && forecast.approbationDate !== undefined;
}

export function selectedIds(rows) {
  return (rows || []).map(row => row.annualLocationForecastId);
}
